import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fallbackDiabetesBlogs } from '../pages/blogs/fallbackDiabetesBlogs';

function endpoint(baseUrl, path) {
  return `${String(baseUrl).replace(/\/$/, '')}${path}`;
}

async function parseJson(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || 'Unexpected server response.' };
  }
}

export const fetchBlogsPage = createAsyncThunk(
  'blogs/fetchBlogsPage',
  async ({ apiBaseUrl }, { rejectWithValue }) => {
    try {
      const response = await fetch(endpoint(apiBaseUrl, '/blog-page'), {
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      const payload = await parseJson(response);

      if (!response.ok || !payload.data) {
        throw new Error(payload.message || 'Unable to load page content.');
      }

      return {
        apiBaseUrl,
        pageContent: payload.data,
        blogs: Array.isArray(payload.data.blogs) ? payload.data.blogs : [],
        isFallback: false,
      };
    } catch (error) {
      return {
        apiBaseUrl,
        pageContent: null,
        blogs: fallbackDiabetesBlogs,
        isFallback: true,
      };
    }
  },
  {
    condition: ({ apiBaseUrl }, { getState }) => {
      const { blogs } = getState();
      return !(blogs.status === 'loading' || (blogs.fetched && blogs.apiBaseUrl === apiBaseUrl));
    },
  }
);

export const fetchBlogDetail = createAsyncThunk(
  'blogs/fetchBlogDetail',
  async ({ apiBaseUrl, slug }, { rejectWithValue }) => {
    try {
      const response = await fetch(endpoint(apiBaseUrl, `/blogs/public/${slug}`), {
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      const payload = await parseJson(response);

      if (!response.ok || !payload.data) {
        throw new Error(payload.message || 'Blog not found.');
      }

      return {
        slug,
        blog: payload.data,
      };
    } catch (error) {
      const fallbackBlog = fallbackDiabetesBlogs.find((blog) => blog.slug === slug);
      if (fallbackBlog) {
        return {
          slug,
          blog: fallbackBlog,
        };
      }
      return rejectWithValue({
        slug,
        message: error.message || 'Unable to load blog.',
      });
    }
  },
  {
    condition: ({ slug }, { getState }) => {
      const { blogs } = getState();
      const existingBlog = blogs.items.find((item) => item.slug === slug);
      if (existingBlog?.content) {
        return false;
      }
      return blogs.detailStatusBySlug[slug] !== 'loading';
    },
  }
);

const initialState = {
  apiBaseUrl: null,
  pageContent: null,
  items: [],
  fetched: false,
  status: 'idle',
  error: '',
  isFallback: false,
  detailStatusBySlug: {},
  detailErrorsBySlug: {},
};

const blogsSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlogsPage.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchBlogsPage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = '';
        state.fetched = true;
        state.apiBaseUrl = action.payload.apiBaseUrl;
        state.pageContent = action.payload.pageContent;
        state.items = action.payload.blogs;
        state.isFallback = Boolean(action.payload.isFallback);
      })
      .addCase(fetchBlogsPage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message || 'Unable to load blogs.';
      })
      .addCase(fetchBlogDetail.pending, (state, action) => {
        state.detailStatusBySlug[action.meta.arg.slug] = 'loading';
        state.detailErrorsBySlug[action.meta.arg.slug] = '';
      })
      .addCase(fetchBlogDetail.fulfilled, (state, action) => {
        const { slug, blog } = action.payload;
        const existingIndex = state.items.findIndex((item) => item.slug === slug);

        if (existingIndex >= 0) {
          state.items[existingIndex] = {
            ...state.items[existingIndex],
            ...blog,
          };
        } else {
          state.items.push(blog);
        }

        state.detailStatusBySlug[slug] = 'succeeded';
        state.detailErrorsBySlug[slug] = '';
      })
      .addCase(fetchBlogDetail.rejected, (state, action) => {
        const slug = action.meta.arg.slug;
        state.detailStatusBySlug[slug] = 'failed';
        state.detailErrorsBySlug[slug] = action.payload?.message || action.error.message || 'Unable to load blog.';
      });
  },
});

export default blogsSlice.reducer;
